import { Injectable } from '@angular/core';
import { Actions, act, createEffect, ofType } from '@ngrx/effects';
import { DownloadFileSystemAppService, InputAppDataService, RootCauseMappingDataService } from '@monorepo/services';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  InputAppMasterdataListDBActionTypes,
  NoMasterdataAction,
  LoadSilhouetteInfo,
  LoadAllMasterdataLists,
  DownloadSilhouetteAssets,
  WriteSilhoutteAssetsInAndroidOriOSStorage,
  InitializeMasterdataListStore,
  SetSilhouetteInfoAndDownloadSilhouetteAssets,
  SetMasterdataListsStore,
  SetAllMasterdataListsAndLoadSilhouetteInfo,
  SetSilhouetteInfo,
  AddOrUpdateMasterdataListByName,
  UpdateMasterdataListStore,
  LoadCorrectiveReasons,
  LoadCorrectiveReasonsSuccess,
  SetMasterdataListsStoreAndLoadAllMasterdataListsAndSilhouetteKeys,
  UpdateFilteredLists,
  UpdateFilteredListsSuccess,
  LoadOperationsAndIssuesV2,
  SetAllMasterdataListsV2AndLoadAllMasterdataLists,
  LoadSilhouetteOperations,
  SetSilhouetteInfoAndLoadSilhouetteOperations,
  UpdateMasterdataListStoreAndDownloadSilhouetteAssets,
  UpdateFilteredRejectList,
  UpdateFilteredListsV3,
  UpdateFilteredListsV3Success,
  UpdateMasterdataListStoreAndEmitSizeListLoadedSubscription,
  LoadRCAHierarchicalData,
} from './master-data-list-db.actions';
import { BCTXComputationHelper, InputAppAPIHelperUtil, InputAppErrorDisplay, MasterdataUtil } from '@monorepo/utils';
import { Platform } from '@ionic/angular';
import { getMasterdataListInitialState } from './master-data-list-db.reducer';
import { MasterdataListDBFacade } from './master-data-list-db.facade';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import produce from 'immer';
import { InitStoreFacade } from '../init-db/init-db.facade';
import {
  OPERATION_ISSUE_SYNCING_KEY,
  RCA_HEIRARCHICAL_DATA_SYNCING_KEY,
  SILHOUETTES_SYNCING_KEY,
  TREQUEST_GetBulkSilhouetteOperations,
} from '@monorepo/types';
import { InputAppMetadataDBFacade } from '../metadata/metadata.facade';
import { UpdateBulkSyncingStatus, UpdateSyncingStatus } from '../metadata/metadata.actions';
import { TREQUEST_GetSilhouetteOperations, TREQUEST_masterdataListsV2 } from '@monorepo/types';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { CutPanelInspectionDBFacade } from '../cut-panel-inspection-db/cut-panel-inspection.facade';

@Injectable({
  providedIn: 'root',
})
export class InputAppMasterdataDBEffects {
  constructor(
    private action$: Actions,
    private persistentStorageService: PersistentStorageService,
    private inputAppDataService: InputAppDataService,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private inputAppErrorUtil: InputAppErrorDisplay,
    private platform: Platform,
    private masterdataListDBFacade: MasterdataListDBFacade,
    private downloadFileSystemAppService: DownloadFileSystemAppService,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private initStoreFacade: InitStoreFacade,
    private metadataFacade: InputAppMetadataDBFacade,
    private translate: TranslateService,
    private cpiDBFacade: CutPanelInspectionDBFacade,
    private rootCauseMappingDataService: RootCauseMappingDataService,
  ) {}

  InitializeMasterdataListStore$ = createEffect(() =>
    this.action$.pipe(
      ofType<InitializeMasterdataListStore>(InputAppMasterdataListDBActionTypes.InitializeMasterdataListStore),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheRestTimestamp) => [action, nextCacheRestTimestamp] as const),
        );
      }),
      switchMap(([action, nextCacheRestTimestamp]) =>
        from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
          take(1),
          switchMap((res) => {
            if (res && res.nextCacheResetTimestamp === nextCacheRestTimestamp)
              return of(new SetMasterdataListsStore(res, action.chainWithInitClientDB, action.chainWithInitCpiDB, action.chainWithLoadRCAHierarchicalData));
            else {
              const masterdataListDBInitialState = getMasterdataListInitialState(nextCacheRestTimestamp);
              const updatedMasterdataListDB = produce(masterdataListDBInitialState, (draft) => {
                if (res) {
                  //This is needed as we persist the filtered operations, issues across days and flush unnecessary data
                  // when we sync them again
                  draft.allMasterdataListsByBctx = res.allMasterdataListsByBctx;
                  draft.masterdataListV2 = res.masterdataListV2;
                  draft.rcaHierarchicalData = res.rcaHierarchicalData;
                }
              });
              return from(
                this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedMasterdataListDB),
              ).pipe(
                take(1),
                map(() => {
                  return new SetMasterdataListsStore(
                    updatedMasterdataListDB,
                    action.chainWithInitClientDB,
                    action.chainWithInitCpiDB,
                    action.chainWithLoadRCAHierarchicalData
                  );
                }),
              );
            }
          }),
        ),
      ),
    ),
  );

  SetMasterdataListsStore$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetMasterdataListsStore>(InputAppMasterdataListDBActionTypes.SetMasterdataListsStore),
      switchMap((action) => {
        if (action.chainWithInitClientDB) {
          this.inputAppClientDBFacade.InitializeClientDB();
        } else if (action.chainWithInitCpiDB) {
          this.cpiDBFacade.initializeCutPanelInspectionDB();
        }

        if (action.chainWithLoadRCAHierarchicalData) {
          this.masterdataListDBFacade.LoadRCAHierarchicalData(true, '');
        }
        return of(new NoMasterdataAction());
      }),
    ),
  );

  LoadRCAHierarchicalData$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadRCAHierarchicalData>(InputAppMasterdataListDBActionTypes.LoadRCAHierarchicalData),
      switchMap((action) => {
        this.metadataFacade.updateSyncingStatus(RCA_HEIRARCHICAL_DATA_SYNCING_KEY, 'SYNCING');
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'LOAD_RCA_HIERARCHICAL_DATA',
            this.translate.instant('input_app_master_data_effects.loadingRCAHierarchicalData'),
          ),
        ).pipe(
          take(1),
          map(() => {
            return action;
          }),
        );
      }),
      switchMap((action) =>
        this.inputAppClientDBFacade.getSubjectKey().pipe(
          take(1),
          map((factoryId) => {
            if (action.init) return [factoryId] as const;
            else return [action.factoryId] as const;
          }),
        ),
      ),
      switchMap(([factoryId]) => {
        return this.rootCauseMappingDataService
          .getHierarchicalData(
            factoryId,
          )
          .pipe(
            switchMap((response) =>
              from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
                take(1),
                switchMap((res) => {
                  const updatedDB = produce(res, (draft) => {
                    if (response) draft.rcaHierarchicalData.categories = response.categories;
                  });
                  return from(this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDB)).pipe(
                    take(1),
                    switchMap(() => {
                      return from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_RCA_HIERARCHICAL_DATA')).pipe(
                        map(() => {
                          this.metadataFacade.updateSyncingStatus(RCA_HEIRARCHICAL_DATA_SYNCING_KEY, 'SYNCED');
                          return new UpdateMasterdataListStore(updatedDB);
                        }),
                      );
                    }),
                  );
                }),
              ),
            ),
            catchError(() =>
              from(
                this.inputAppAPIHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_effects.fetchingRCAHierarchicalDataError.header'),
                  this.translate.instant('input_app_master_data_effects.fetchingRCAHierarchicalDataError.message'),
                ),
              ).pipe(
                switchMap(() =>
                  from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_RCA_HIERARCHICAL_DATA')).pipe(
                    map(() => new UpdateSyncingStatus(RCA_HEIRARCHICAL_DATA_SYNCING_KEY, 'FAILED')),
                  ),
                ),
              ),
            ),
          );
      }),
    ),
  );

  LoadSilhouetteInfo$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadSilhouetteInfo>(InputAppMasterdataListDBActionTypes.LoadSilhouetteInfo),
      switchMap((payload) => {
        this.metadataFacade.updateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'SYNCING');
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'LOAD_SILHOUETTE_INFO',
            this.translate.instant('input_app_master_data_effects.loadingSilhouetteList'),
          ),
        ).pipe(
          take(1),
          map(() => {
            return payload;
          }),
        );
      }),
      switchMap((payload) =>
        this.masterdataListDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map((order) => {
            return [order, payload] as const;
          }),
        ),
      ),
      switchMap(([order, payload]) =>
        this.inputAppClientDBFacade.getConfigurations().pipe(
          take(1),
          map(({ useOperationSilhouetteMapping }) => {
            return [order, payload, useOperationSilhouetteMapping] as const;
          }),
        ),
      ),
      switchMap(([order, action, useOperationSilhouetteMapping]) => {
        return this.inputAppDataService
          .getSilhouetteKeys(
            MasterdataUtil.getSilhouetteKeysPayload(order, action.bctxLists, action.subjectKey, action.listConf),
          )
          .pipe(
            switchMap((response) =>
              from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
                take(1),
                switchMap((res) => {
                  const updatedDB = MasterdataUtil.getUpdatedDBWithSilhouetteKeys(res, response);
                  return from(this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDB)).pipe(
                    take(1),
                    switchMap((res) => {
                      return from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_SILHOUETTE_INFO')).pipe(
                        map(() => {
                          if (!useOperationSilhouetteMapping) {
                            if (!this.inputAppClientDBFacade.isWeb()) {
                              return new SetSilhouetteInfoAndDownloadSilhouetteAssets(updatedDB['silhouetteKeysInfo']);
                            } else {
                              this.metadataFacade.updateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'SYNCED');
                              return new SetSilhouetteInfo(updatedDB['silhouetteKeysInfo']);
                            }
                          } else
                            return new SetSilhouetteInfoAndLoadSilhouetteOperations(
                              updatedDB['silhouetteKeysInfo'],
                              action.bctxLists,
                              action.listConf,
                              action.subjectKey,
                            );
                        }),
                      );
                    }),
                  );
                }),
              ),
            ),
            catchError((error) =>
              from(
                this.inputAppAPIHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_effects.silhouetteListError.header'),
                  this.translate.instant('input_app_master_data_effects.silhouetteListError.message'),
                ),
              ).pipe(
                switchMap(() =>
                  from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_SILHOUETTE_INFO')).pipe(
                    map(() => new UpdateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'FAILED')),
                  ),
                ),
              ),
            ),
          );
      }),
    ),
  );

  SetSilhouetteInfoAndLoadSilhouetteOperations$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetSilhouetteInfoAndLoadSilhouetteOperations>(
        InputAppMasterdataListDBActionTypes.SetSilhouetteInfoAndLoadSilhouetteOperations,
      ),
      map((action) => new LoadSilhouetteOperations(action.bctxLists, action.silhouetteListConf, action.subjectKey)),
    ),
  );

  LoadSilhouetteOperations$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadSilhouetteOperations>(InputAppMasterdataListDBActionTypes.LoadSilhouetteOperations),
      switchMap((payload) =>
        from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'LOAD_SILHOUETTE_OPERATIONS',
            this.translate.instant('input_app_master_data_effects.loadingSilhouetteOperations'),
          ),
        ).pipe(
          take(1),
          map(() => {
            return payload;
          }),
        ),
      ),
      switchMap((payload) =>
        this.inputAppClientDBFacade.getConfigurations().pipe(
          take(1),
          map(({ operationsAndIssuesDepartmentId, operationsAndIssuesAllDepartmentIds }) => {
            const allOperationIssueDepartments = MasterdataUtil.getAllOperationIssueDepartments(
              operationsAndIssuesDepartmentId,
              operationsAndIssuesAllDepartmentIds,
            );
            return [payload, allOperationIssueDepartments] as const;
          }),
        ),
      ),
      switchMap(([payload, operationsAndIssuesDepartmentId]) =>
        this.masterdataListDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map((order) => {
            return [order, payload, operationsAndIssuesDepartmentId] as const;
          }),
        ),
      ),
      switchMap(([order, payload, operationsAndIssuesDepartmentId]) =>
        from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
          take(1),
          map((masterdataListDB) => {
            return [order, payload, masterdataListDB, operationsAndIssuesDepartmentId] as const;
          }),
        ),
      ),
      switchMap(([order, action, masterdataListDB, operationsAndIssuesDepartmentId]) => {
        const keys = MasterdataUtil.getSilhouetteKeys(order, action.bctxLists, action.silhouetteListConf);
        const ids = keys.reduce((acc, { key, productType, style }) => {
          if (masterdataListDB.silhouetteKeysInfo.keys[key]) {
            acc.push({
              style,
              productType,
              ...MasterdataUtil.getFrontAndBackImageIds(masterdataListDB.silhouetteKeysInfo.keys[key]),
            });
          }
          return acc;
        }, [] as TREQUEST_GetBulkSilhouetteOperations['bulkSilhouetteOperationIds']);
        return from(
          this.inputAppDataService.getBulkSilhouetteOperations({
            factoryId: action.subjectKey,
            bulkSilhouetteOperationIds: ids,
            departments: operationsAndIssuesDepartmentId,
          }),
        ).pipe(
          take(1),
          switchMap((res) => {
            Object.keys(res).forEach((department) => {
              res[department].silhouetteOperations.forEach(
                ({ productType, style, frontImageOperation, backImageOperation }) => {
                  const currentTime = new Date().getTime();
                  masterdataListDB = produce(masterdataListDB, (draft) => {
                    if (!draft.masterdataListV2[department]) {
                      draft.masterdataListV2[department] = {
                        genericIssues: {
                          filteredDefectList: [],
                          filteredRejectList: [],
                          list: [],
                        },
                        listByProductTypeAndStyle: {
                          [productType]: {},
                        },
                      };
                    }
                  });
                  masterdataListDB = produce(masterdataListDB, (draft) => {
                    if (!draft.masterdataListV2[department].listByProductTypeAndStyle[productType]) {
                      draft.masterdataListV2[department].listByProductTypeAndStyle[productType] = {};
                    }
                  });
                  masterdataListDB = produce(masterdataListDB, (draft) => {
                    if (!draft.masterdataListV2[department].listByProductTypeAndStyle[productType][style]) {
                      draft.masterdataListV2[department].listByProductTypeAndStyle[productType][style] = {
                        operations: {
                          lastSyncedAt: currentTime,
                          list: [],
                          filteredOperationsList: [],
                        },
                        silhouetteOperations: {
                          frontImageOperations: frontImageOperation.silhouetteOperations,
                          backImageOperations: backImageOperation.silhouetteOperations,
                        },
                      };
                    } else {
                      draft.masterdataListV2[department].listByProductTypeAndStyle[productType][
                        style
                      ].silhouetteOperations.frontImageOperations = frontImageOperation.silhouetteOperations;
                      draft.masterdataListV2[department].listByProductTypeAndStyle[productType][
                        style
                      ].silhouetteOperations.backImageOperations = backImageOperation.silhouetteOperations;
                    }
                  });
                },
              );
            });
            return from(this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', masterdataListDB)).pipe(
              take(1),
              map(() => {
                this.inputAppErrorUtil.dismissLoadingWithId('LOAD_SILHOUETTE_OPERATIONS');
                if (this.inputAppClientDBFacade.isWeb()) {
                  this.metadataFacade.updateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'SYNCED');
                  return new UpdateMasterdataListStore(masterdataListDB);
                } else {
                  return new UpdateMasterdataListStoreAndDownloadSilhouetteAssets(masterdataListDB);
                }
              }),
            );
          }),
          catchError((error) => {
            return from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_SILHOUETTE_OPERATIONS')).pipe(
              take(1),
              map(() => {
                this.inputAppAPIHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_effects.silhouetteOperationsError.header'),
                  this.translate.instant('input_app_master_data_effects.silhouetteOperationsError.message'),
                );
                return new UpdateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'FAILED');
              }),
            );
          }),
        );
      }),
    ),
  );

  UpdateMasterdataListStoreAndDownloadSilhouetteAssets$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateMasterdataListStoreAndDownloadSilhouetteAssets>(
        InputAppMasterdataListDBActionTypes.UpdateMasterdataListStoreAndDownloadSilhouetteAssets,
      ),
      map(() => new DownloadSilhouetteAssets()),
    ),
  );

  SetSilhouetteInfoAndDownloadSilhouetteAssets$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetSilhouetteInfoAndDownloadSilhouetteAssets>(
        InputAppMasterdataListDBActionTypes.SetSilhouetteInfoAndDownloadSilhouetteAssets,
      ),
      map(() => new DownloadSilhouetteAssets()),
    ),
  );

  DownloadSilhouetteAssets$ = createEffect(() =>
    this.action$.pipe(
      ofType<DownloadSilhouetteAssets>(InputAppMasterdataListDBActionTypes.DownloadSilhouetteAssets),
      switchMap(() =>
        this.masterdataListDBFacade.getDownloadSilhouetteAssetsPayload().pipe(
          take(1),
          map((payload) => payload),
        ),
      ),
      switchMap((payload) =>
        from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'DOWNLOAD_SILHOUETTE_ASSETS',
            this.translate.instant('input_app_master_data_effects.downloadingSilhouetteImages'),
          ),
        ).pipe(
          take(1),
          map(() => payload),
        ),
      ),
      switchMap((payload) =>
        this.inputAppDataService.downloadSilhoutte(payload).pipe(
          switchMap((res) =>
            from(this.inputAppErrorUtil.dismissLoadingWithId('DOWNLOAD_SILHOUETTE_ASSETS')).pipe(
              take(1),
              map(() => new WriteSilhoutteAssetsInAndroidOriOSStorage(res)),
            ),
          ),
          catchError((error) =>
            from(this.inputAppErrorUtil.dismissLoadingWithId('DOWNLOAD_SILHOUETTE_ASSETS')).pipe(
              take(1),
              map(() => {
                this.inputAppAPIHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_effects.downloadingSilhouetteImagesError.header'),
                  this.translate.instant('input_app_master_data_effects.downloadingSilhouetteImagesError.message'),
                );
                return new UpdateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'FAILED');
              }),
            ),
          ),
        ),
      ),
    ),
  );

  WriteSilhoutteAssetsInAndroidOriOSStorage$ = createEffect(() =>
    this.action$.pipe(
      ofType<WriteSilhoutteAssetsInAndroidOriOSStorage>(
        InputAppMasterdataListDBActionTypes.WriteSilhoutteAssetsInAndroidOriOSStorage,
      ),
      switchMap((action) =>
        from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'WRITE_SILHOUETTE_IMAGES',
            this.translate.instant('input_app_master_data_effects.writingAssetsInStorage'),
          ),
        ).pipe(
          take(1),
          map(() => {
            return action.data;
          }),
        ),
      ),
      switchMap((data) =>
        from(this.downloadFileSystemAppService.writeSilhoutteAssetsInApp(data)).pipe(
          switchMap((isSuccess) =>
            from(this.inputAppErrorUtil.dismissLoadingWithId('WRITE_SILHOUETTE_IMAGES')).pipe(
              take(1),
              map(() => {
                if (!isSuccess) {
                  this.inputAppAPIHelperUtil.showPopup(
                    this.translate.instant('input_app_master_data_effects.writingAssetsInStorageError.header'),
                    this.translate.instant('input_app_master_data_effects.writingAssetsInStorageError.message'),
                  );
                  this.metadataFacade.updateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'FAILED');
                } else this.metadataFacade.updateSyncingStatus(SILHOUETTES_SYNCING_KEY, 'SYNCED');
                return new NoMasterdataAction();
              }),
            ),
          ),
        ),
      ),
    ),
  );

  LoadAllMasterdataListsV2$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadOperationsAndIssuesV2>(InputAppMasterdataListDBActionTypes.LoadOperationsAndIssuesV2),
      switchMap((payload) => {
        this.metadataFacade.updateSyncingStatus(OPERATION_ISSUE_SYNCING_KEY, 'SYNCING');
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'LOAD_OPERATION_ISSUE_MAPPING',
            this.translate.instant('input_app_master_data_effects.loadingOperationsAndIssues'),
          ),
        ).pipe(
          take(1),
          map(() => {
            return payload;
          }),
        );
      }),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
          take(1),
          map((res) => {
            return [action, res] as const;
          }),
        ),
      ),
      switchMap(([action, currentDB]) => {
        return this.inputAppDataService
          .getAllMasterdataListsV2({
            factoryId: action.subjectKey,
            departments: action.departments,
            productTypeStyles: this.masterdataListDBFacade.getUniqueProductTypeAndStyles(action.bctxLists),
          })
          .pipe(
            switchMap((masterdataListResponse) => {
              const updatedDBWithMasterdataLists = MasterdataUtil.getUpdatedDBWithAllMasterdataListsV2(
                currentDB,
                masterdataListResponse,
              );
              this.metadataFacade.updateSyncingStatus(OPERATION_ISSUE_SYNCING_KEY, 'SYNCED');
              return from(
                this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDBWithMasterdataLists),
              ).pipe(
                switchMap(() =>
                  from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_OPERATION_ISSUE_MAPPING')).pipe(
                    map(() => {
                      if (!action.chainWithLoadAllMasterdataLists) {
                        return new UpdateMasterdataListStore(updatedDBWithMasterdataLists);
                      } else {
                        return new SetAllMasterdataListsV2AndLoadAllMasterdataLists(
                          updatedDBWithMasterdataLists,
                          action.chainWithLoadSilhouetteInfo,
                          action.bctxLists,
                          action.masterdataListKeys,
                          action.subjectKey,
                          action.masterdataListConf,
                          action.silhouetteListConf,
                          action.masterDataListMetaData,
                        );
                      }
                    }),
                  ),
                ),
              );
            }),
            catchError((error) =>
              from(
                this.inputAppAPIHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.header'),
                  this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.message'),
                ),
              ).pipe(
                switchMap(() =>
                  from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_OPERATION_ISSUE_MAPPING')).pipe(
                    map(() => {
                      this.metadataFacade.updateSyncingStatus(OPERATION_ISSUE_SYNCING_KEY, 'FAILED');
                      return new NoMasterdataAction();
                    }),
                  ),
                ),
              ),
            ),
          );
      }),
    ),
  );

  SetAllMasterdataListsV2AndLoadAllMasterdataLists$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetAllMasterdataListsV2AndLoadAllMasterdataLists>(
        InputAppMasterdataListDBActionTypes.SetAllMasterdataListsV2AndLoadAllMasterdataLists,
      ),
      map((action) => {
        if (action.masterdataListKeys.length > 0) {
          return new LoadAllMasterdataLists(
            action.chainWithLoadSilhouetteInfo,
            action.bctxLists,
            action.masterdataListKeys,
            action.subjectKey,
            action.masterdataListConf,
            action.silhouetteListConf,
            action.masterDataListMetaData,
          );
        } else if (action.chainWithLoadSilhouetteInfo) {
          return new LoadSilhouetteInfo(action.bctxLists, action.subjectKey, action.silhouetteListConf);
        } else return new NoMasterdataAction();
      }),
    ),
  );

  LoadAllMasterdataListsAndSilhouetteKeys$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadAllMasterdataLists>(InputAppMasterdataListDBActionTypes.LoadAllMasterdataLists),
      switchMap((payload) => {
        this.metadataFacade.updateBulkSyncingStatus(payload.masterdataListKeys, 'SYNCING');
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'LOADING_ALL_MASTER_DATA_LIST_WITH_SILHOUETTE',
            this.translate.instant('input_app_master_data_effects.loadingMasterDataList'),
          ),
        ).pipe(
          take(1),
          map(() => {
            return payload;
          }),
        );
      }),
      switchMap((payload) =>
        this.masterdataListDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map((order) => {
            return [order, payload] as const;
          }),
        ),
      ),
      switchMap(([order, action]) =>
        from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
          take(1),
          map((res) => {
            return [action, res, order] as const;
          }),
        ),
      ),
      switchMap(([action, currentDB, order]) => {
        return this.inputAppDataService
          .getAllMasterdataLists(
            MasterdataUtil.getAllMasterdataListsPayload(
              order,
              action.bctxLists,
              action.masterdataListKeys,
              action.subjectKey,
              action.masterdataListConf,
            ),
          )
          .pipe(
            switchMap((masterdataListResponse) => {
              const updatedDBWithMasterdataLists = MasterdataUtil.getUpdatedDBWithAllMasterdataLists(
                currentDB,
                masterdataListResponse,
              );
              return from(
                this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDBWithMasterdataLists),
              ).pipe(
                switchMap(() =>
                  from(
                    this.inputAppErrorUtil.dismissLoadingWithId('LOADING_ALL_MASTER_DATA_LIST_WITH_SILHOUETTE'),
                  ).pipe(
                    map(() => {
                      this.metadataFacade.updateBulkSyncingStatus(action.masterdataListKeys, 'SYNCED');
                      if (!action.chainWithLoadSilhouetteInfo) {
                        this.masterdataListDBFacade.updateMasterDataListLoaded();
                        return new UpdateMasterdataListStoreAndEmitSizeListLoadedSubscription(
                          updatedDBWithMasterdataLists,
                          true,
                          action.masterDataListMetaData ? action.masterDataListMetaData : { source: 'UNKNOWN' },
                        );
                      } else {
                        return new SetAllMasterdataListsAndLoadSilhouetteInfo(
                          updatedDBWithMasterdataLists,
                          action.bctxLists,
                          action.subjectKey,
                          action.silhouetteListConf,
                          action.masterDataListMetaData,
                        );
                      }
                    }),
                  ),
                ),
              );
            }),
            catchError((error) =>
              from(
                this.inputAppAPIHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.header'),
                  this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.message'),
                ),
              ).pipe(
                switchMap(() =>
                  from(
                    this.inputAppErrorUtil.dismissLoadingWithId('LOADING_ALL_MASTER_DATA_LIST_WITH_SILHOUETTE'),
                  ).pipe(map(() => new UpdateBulkSyncingStatus(action.masterdataListKeys, 'FAILED'))),
                ),
              ),
            ),
          );
      }),
    ),
  );

  AddOrUpdateMasterdataListByName$ = createEffect(() =>
    this.action$.pipe(
      ofType<AddOrUpdateMasterdataListByName>(InputAppMasterdataListDBActionTypes.AddOrUpdateMasterdataListByName),
      switchMap((payload) => {
        this.metadataFacade.updateSyncingStatus(payload.appSyncScreenItem.value, 'SYNCING');
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'UPDATE_MASTER_DATA_LIST',
            this.translate.instant('input_app_master_data_effects.loadingMasterDataList'),
          ),
        ).pipe(
          take(1),
          map(() => {
            return payload;
          }),
        );
      }),
      switchMap((payload) =>
        this.masterdataListDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map((order) => {
            return [order, payload] as const;
          }),
        ),
      ),
      switchMap(([order, action]) =>
        from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
          take(1),
          map((res) => {
            return [action, res, order] as const;
          }),
        ),
      ),
      switchMap(([action, currentDB, order]) => {
        return this.inputAppDataService
          .getAllMasterdataLists(
            MasterdataUtil.getAllMasterdataListsPayload(
              order,
              action.bctxLists,
              [action.appSyncScreenItem.value],
              action.subjectKey,
              action.masterdataListConf,
            ),
          )
          .pipe(
            switchMap((masterdataListResponse) => {
              const updatedDBWithMasterdataLists = MasterdataUtil.getUpdatedDBWithMasterdataListRefetching(
                currentDB,
                masterdataListResponse,
                action.appSyncScreenItem.value,
              );
              return from(
                this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDBWithMasterdataLists),
              ).pipe(
                switchMap(() =>
                  from(this.inputAppErrorUtil.dismissLoadingWithId('UPDATE_MASTER_DATA_LIST')).pipe(
                    map(() => {
                      this.metadataFacade.updateSyncingStatus(action.appSyncScreenItem.value, 'SYNCED');
                      return new UpdateMasterdataListStoreAndEmitSizeListLoadedSubscription(
                        updatedDBWithMasterdataLists,
                        action.appSyncScreenItem.id === 'size_list',
                        { source: 'UNKNOWN' },
                      );
                    }),
                  ),
                ),
              );
            }),
            catchError((error) =>
              from(
                this.inputAppAPIHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.header'),
                  this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.message'),
                ),
              ).pipe(
                switchMap(() =>
                  from(this.inputAppErrorUtil.dismissLoadingWithId('UPDATE_MASTER_DATA_LIST')).pipe(
                    map(() => new UpdateSyncingStatus(action.appSyncScreenItem.value, 'FAILED')),
                  ),
                ),
              ),
            ),
          );
      }),
    ),
  );

  SetAllMasterdataListsAndLoadSilhouetteInfo$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetAllMasterdataListsAndLoadSilhouetteInfo>(
        InputAppMasterdataListDBActionTypes.SetAllMasterdataListsAndLoadSilhouetteInfo,
      ),
      map((action) => {
        this.masterdataListDBFacade.emitMasterdataListLoadedsSubscription(action.masterDataListMetaData);
        return new LoadSilhouetteInfo(action.bctxLists, action.subjectKey, action.listConf);
      }),
    ),
  );

  LoadCorrectiveReasons$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadCorrectiveReasons>(InputAppMasterdataListDBActionTypes.LoadCorrectiveReasons),
      switchMap((action) =>
        this.masterdataListDBFacade.getLoadCorrectiveReasonsPayload().pipe(
          take(1),
          map((payload) => [action, payload] as const),
        ),
      ),
      switchMap(([action, payload]) => {
        this.metadataFacade.updateSyncingStatus(action.listKey, 'SYNCING');
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'LOAD_CORRECTIVE_REASONS',
            this.translate.instant('input_app_master_data_effects.loadingCorrectiveReasons'),
          ),
        ).pipe(
          take(1),
          map(() => [action, payload] as const),
        );
      }),
      switchMap(([action, payload]) =>
        this.inputAppDataService.getCorrectiveReasons(payload).pipe(
          switchMap((res) =>
            from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_CORRECTIVE_REASONS')).pipe(
              take(1),
              map(() => {
                this.metadataFacade.updateSyncingStatus(action.listKey, 'SYNCED');
                return new LoadCorrectiveReasonsSuccess(
                  res,
                  action.chainWithLoadAllMasterdataListsAndSilhouetteKeys,
                  action.masterDataListMetaData,
                );
              }),
            ),
          ),
          catchError((error) =>
            from(
              this.inputAppAPIHelperUtil.showPopup(
                this.translate.instant('input_app_master_data_effects.loadingCorrectiveReasonsError.header'),
                this.translate.instant('input_app_master_data_effects.loadingCorrectiveReasonsError.message'),
              ),
            ).pipe(
              switchMap(() =>
                from(this.inputAppErrorUtil.dismissLoadingWithId('LOAD_CORRECTIVE_REASONS')).pipe(
                  map(() => new UpdateSyncingStatus(action.listKey, 'FAILED')),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  LoadCorrectiveReasonsSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadCorrectiveReasonsSuccess>(InputAppMasterdataListDBActionTypes.LoadCorrectiveReasonsSuccess),
      switchMap((payload) =>
        from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'LOADING_CORRECTIVE_REASONS_SUCCESS',
            this.translate.instant('input_app_master_data_effects.updatingCorrectiveReasons'),
          ),
        ).pipe(
          take(1),
          map(() => payload),
        ),
      ),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][MASTER_DATA_LIST_DB]')).pipe(
          take(1),
          map((masterDataListDB) => {
            return [
              action,
              produce(masterDataListDB, (draft) => {
                draft.correctiveReasons = action.res;
              }),
            ] as const;
          }),
        ),
      ),
      switchMap(([action, masterDataListDB]) =>
        from(this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', masterDataListDB)).pipe(
          take(1),
          switchMap((masterDataListDB) =>
            from(this.inputAppErrorUtil.dismissLoadingWithId('LOADING_CORRECTIVE_REASONS_SUCCESS')).pipe(
              take(1),
              map(() => {
                if (action.chainWithLoadAllMasterdataListsAndSilhouetteKeys)
                  return new SetMasterdataListsStoreAndLoadAllMasterdataListsAndSilhouetteKeys(
                    masterDataListDB,
                    action.masterDataListMetaData,
                  );
                else return new SetMasterdataListsStore(masterDataListDB);
              }),
            ),
          ),
        ),
      ),
    ),
  );

  SetMasterdataListsStoreAndLoadAllMasterdataListsAndSilhouetteKeys$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetMasterdataListsStoreAndLoadAllMasterdataListsAndSilhouetteKeys>(
        InputAppMasterdataListDBActionTypes.SetMasterdataListsStoreAndLoadAllMasterdataListsAndSilhouetteKeys,
      ),
      switchMap((action) => {
        this.masterdataListDBFacade.loadAllMasterdataLists(true, action.masterDataListMetaData);
        return of(new NoMasterdataAction());
      }),
    ),
  );

  UpdateFilteredLists$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateFilteredLists>(InputAppMasterdataListDBActionTypes.UpdateFilteredLists),
      switchMap((action) =>
        from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'UPDATE_FILTERED_LISTS',
            this.translate.instant('input_app_master_data_effects.updatingFilteredLists'),
          ),
        ).pipe(
          take(1),
          map(() => action),
        ),
      ),
      switchMap((action) => {
        return this.masterdataListDBFacade
          .getUpdatedDBWithModifiedFilteredLists(action.id, action.bctx, action.list)
          .pipe(
            take(1),
            switchMap((updatedDB) => {
              return from(this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDB)).pipe(
                take(1),
                map(() => {
                  this.inputAppErrorUtil.dismissLoadingWithId('UPDATE_FILTERED_LISTS');
                  return new UpdateFilteredListsSuccess(updatedDB);
                }),
              );
            }),
          );
      }),
    ),
  );

  UpdateFilteredListsSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateFilteredListsSuccess>(InputAppMasterdataListDBActionTypes.UpdateFilteredListsSuccess),
      switchMap((action) => {
        this.masterdataListDBFacade.emitFilteredListsUpdate();
        return of(new NoMasterdataAction());
      }),
    ),
  );

  UpdateFilteredListsV3$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateFilteredListsV3>(InputAppMasterdataListDBActionTypes.UpdateFilteredListsV3),
      switchMap((action) =>
        from(
          this.inputAppAPIHelperUtil.presentLoading(
            this.translate.instant('input_app_master_data_effects.updatingFilteredLists'),
          ),
        ).pipe(
          take(1),
          map(() => action),
        ),
      ),
      switchMap((action) => {
        return this.masterdataListDBFacade
          .getUpdatedDBWithModifiedFilteredListsV3(action.bctx, action.updatedFilteredListData)
          .pipe(
            take(1),
            switchMap((updatedDB) => {
              return from(this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDB)).pipe(
                take(1),
                map(() => {
                  this.inputAppAPIHelperUtil.dismissLoading();
                  return new UpdateFilteredListsV3Success(updatedDB);
                }),
              );
            }),
          );
      }),
    ),
  );

  UpdateFilteredListsV3Success$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateFilteredListsV3Success>(InputAppMasterdataListDBActionTypes.UpdateFilteredListsV3Success),
      switchMap((action) => {
        this.masterdataListDBFacade.emitFilteredListsUpdate();
        return of(new NoMasterdataAction());
      }),
    ),
  );

  UpdateFilteredRejectList$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateFilteredRejectList>(InputAppMasterdataListDBActionTypes.UpdateFilteredRejectList),
      switchMap((action) =>
        from(
          this.inputAppErrorUtil.presentLoadingWithId('UPDATE_FILTERED_REJECT_LIST', 'Updating filtered list...'),
        ).pipe(
          take(1),
          map(() => [action.filteredList, action.department] as const),
        ),
      ),
      switchMap(([filteredList, department]) =>
        this.masterdataListDBFacade.getUpdatedDBWithModifiedFilteredRejectListV2(filteredList, department).pipe(
          take(1),
          switchMap((updatedDB) => {
            return from(this.persistentStorageService.set('[APP][MASTER_DATA_LIST_DB]', updatedDB)).pipe(
              take(1),
              map(() => {
                this.inputAppErrorUtil.dismissLoadingWithId('UPDATE_FILTERED_REJECT_LIST');
                return new UpdateFilteredListsSuccess(updatedDB);
              }),
            );
          }),
        ),
      ),
    ),
  );
  UpdateMasterdataListStoreAndEmitSizeListLoadedSubscription$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateMasterdataListStoreAndEmitSizeListLoadedSubscription>(
        InputAppMasterdataListDBActionTypes.UpdateMasterdataListStoreAndEmitSizeListLoadedSubscription,
      ),
      switchMap((action) => {
        if (action.isSizeListLoaded) {
          this.masterdataListDBFacade.emitMasterdataListLoadedsSubscription(action.masterDataListMetaData);
          return of(new NoMasterdataAction());
        } else {
          return of(new NoMasterdataAction());
        }
      }),
    );
  });
}
